import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, CheckCircle, Mail } from "lucide-react";
import LoadingSpinner from "../common/LoadingSpinner";
import { getEmailValidationError } from "../../utils/emailValidation";

const AuthCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<"processing" | "success" | "error">(
    "processing",
  );
  const [message, setMessage] = useState<string>("Verifying your email...");
  const [canRecover, setCanRecover] = useState(false);
  const [email, setEmail] = useState("");
  const [resendError, setResendError] = useState("");
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const verifiedTokenRef = useRef<string | null>(null);

  const getApiBase = () => {
    return typeof window !== "undefined" &&
      window.location.hostname === "localhost"
      ? "http://localhost:8000/api/"
      : import.meta.env.VITE_API_URL ||
          "https://pawtected-client-dev-b941ac1d80e9.herokuapp.com/api/";
  };

  const handleFastAPIVerification = async (token: string) => {
    try {
      const baseUrl = getApiBase();

      const response = await fetch(`${baseUrl}auth/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus("success");
        setMessage("Email verified successfully! Redirecting to login...");

        // Store the verified email so login page can pre-fill it
        if (data.email) {
          sessionStorage.setItem("verifiedEmail", data.email);
        }

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } else if (data.detail === "VERIFY_USER_ALREADY_VERIFIED") {
        // A consumed/already-used verification link is not an error — the
        // account is set up. This is the common case when a stale callback URL
        // is reopened (e.g. iOS Safari restoring a tab) or a session lapses.
        // Show a calm message and route to login rather than a red failure.
        setStatus("success");
        setMessage(
          "Your account is already verified. Redirecting you to sign in...",
        );
        setTimeout(() => navigate("/login?hint=session-expired"), 2500);
      } else if (data.detail === "VERIFY_USER_BAD_TOKEN") {
        setStatus("error");
        setCanRecover(true);
        setMessage(
          "This verification link expired or was already used. Enter your email to request a new one.",
        );
      } else {
        setStatus("error");
        setMessage(
          typeof data.detail === "string"
            ? data.detail
            : "Email verification failed. The link may have expired.",
        );
        setTimeout(() => navigate("/login?hint=session-expired"), 4000);
      }
    } catch (error: any) {
      console.error("Verification error:", error);
      setStatus("error");
      setMessage("An error occurred during verification. Please try again.");
      setTimeout(() => navigate("/login"), 5000);
    }
  };

  useEffect(() => {
    const handleEmailVerification = async () => {
      const token = searchParams.get("token");
      if (token) {
        // Prevent duplicate calls for the same token (React StrictMode in dev).
        if (verifiedTokenRef.current === token) {
          console.log("Skipping duplicate verification attempt");
          return;
        }
        verifiedTokenRef.current = token;
        setStatus("processing");
        setCanRecover(false);
        setResent(false);
        setResendError("");
        setMessage("Verifying your email...");
        console.log("FastAPI-users token detected, verifying with backend");
        await handleFastAPIVerification(token);
        return;
      }

      // Handle password reset token (redirect to reset password page)
      const resetToken = searchParams.get("reset_token");
      if (resetToken) {
        console.log("Password reset token detected, redirecting");
        navigate(`/reset-password?token=${resetToken}`, { replace: true });
        return;
      }

      // A token was already processed on this mount (e.g. expired/consumed link
      // showing the recovery form). Do not fall through to the bare-callback
      // redirect, which would overwrite the recovery UX.
      if (verifiedTokenRef.current) {
        return;
      }

      // No recognized params, redirect to login
      console.log("No verification token found, redirecting to login");
      setStatus("error");
      setMessage("Invalid verification link. Redirecting to login...");
      setTimeout(() => navigate("/login"), 3000);
    };

    handleEmailVerification();
  }, [searchParams, navigate]);

  const handleResendVerification = async (event: React.FormEvent) => {
    event.preventDefault();
    setResendError("");

    const emailError = getEmailValidationError(email);
    if (emailError) {
      setResendError(emailError);
      return;
    }

    setResending(true);
    try {
      const response = await fetch(
        `${getApiBase()}users/send-verification-email`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email.trim() }),
        },
      );
      const data = await response.json();
      if (!response.ok) {
        setResendError(
          typeof data.detail === "string"
            ? data.detail
            : "Could not send a verification email. Please try again.",
        );
        return;
      }
      setResent(true);
    } catch (error: any) {
      console.error("Verification resend error:", error);
      setResendError(
        error.message ||
          "Could not send a verification email. Please try again.",
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-6 sm:p-12">
        <div className="flex flex-col items-center">
          {status === "processing" && (
            <>
              <div className="mb-6">
                <LoadingSpinner size="large" text="" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Verifying Email
              </h1>
              <p className="text-gray-600 text-center">{message}</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Email Verified!
              </h1>
              <p className="text-gray-600 text-center">{message}</p>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                <AlertCircle className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Verification Failed
              </h1>
              <p className="text-gray-600 text-center mb-4">{message}</p>
              {canRecover && !resent && (
                <form
                  onSubmit={handleResendVerification}
                  className="w-full space-y-4"
                >
                  {resendError && (
                    <p
                      className="text-sm text-red-700 text-center"
                      role="alert"
                    >
                      {resendError}
                    </p>
                  )}
                  <div>
                    <label
                      htmlFor="recovery-email"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                        aria-hidden="true"
                      />
                      <input
                        id="recovery-email"
                        type="email"
                        value={email}
                        onChange={(event) => {
                          setEmail(event.target.value);
                          setResendError("");
                        }}
                        autoComplete="email"
                        required
                        className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-[color:var(--pmc-accent)]"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={resending}
                    className="w-full px-6 py-3 bg-[color:var(--pmc-primary)] text-white rounded-lg disabled:opacity-50"
                  >
                    {resending ? "Sending..." : "Send new verification email"}
                  </button>
                </form>
              )}
              {canRecover && resent && (
                <div className="w-full rounded-2xl bg-green-50 border border-green-200 p-5 text-center">
                  <CheckCircle
                    className="mx-auto mb-2 h-8 w-8 text-green-600"
                    aria-hidden="true"
                  />
                  <h2 className="font-semibold text-green-800">
                    Request received
                  </h2>
                  <p className="mt-2 text-sm text-green-700">
                    Check your inbox and spam folder for the latest verification
                    email for {email.trim()}. It may take a few minutes to
                    arrive.
                  </p>
                </div>
              )}
              <button
                onClick={() => navigate("/login")}
                className="mt-4 px-6 py-2 bg-[color:var(--pmc-primary)] text-white rounded-lg hover:bg-[color:var(--pmc-primary)] transition-colors"
              >
                {canRecover ? "Back to Sign In" : "Go to Login"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthCallbackPage;
